import { useCallback, useMemo, useState } from 'react';

import { groupApi, type ChinaAppSysGroupViewBody } from '@/api/services/groupApi';
import { viewApi } from '@/api/services/viewApi';
import { CheckBox } from '@/components/ui';
import type { ChinaAppSysGroupView } from '@/types/entities';
import { entityTypeDisplayNames } from '@/types/enums';

import { useAdminChanges } from '../adminChanges';
import { PickerDialog, type PickerOption } from '../PickerDialog';
import { useAdminList } from './adminList';
import { AdminSubList } from './AdminSubList';

/** Views einer Gruppe — Port von `IwmAdminListGroupViews.razor` (REQ-28 §3). */
export interface AdminGroupViewsListProps {
  parentId: number;
}

/** Die fünf Rechte mit den Spaltenüberschriften der heutigen Liste. */
const RIGHTS = [
  { key: 'AllowCreate', label: 'Add' },
  { key: 'AllowEdit', label: 'Update' },
  { key: 'AllowDelete', label: 'Delete' },
  { key: 'AllowRegiezentrum', label: 'Visible in Menu' },
  { key: 'AllowAudit', label: 'Audit' },
] as const;

type RightKey = (typeof RIGHTS)[number]['key'];

const COLUMNS = ['Bezeichnung', ...RIGHTS.map((right) => right.label)];

/**
 * Pendant zu `new ChinaAppSysGroupView() { GroupId = …, ViewId = …, AllowEdit = true,
 * AllowRegiezentrum = true }` — die Vorbelegung des Altclients. Für diese Entität gibt es
 * keine Fabrik in `@/types/entities`.
 */
function createGroupView(groupId: number, viewId: number): ChinaAppSysGroupViewBody {
  return {
    Id: 0,
    // `default(DateTime)` so, wie System.Text.Json den Wert serialisiert; eine leere
    // Zeichenkette lehnt die API als `DateTime` ab.
    Created: '0001-01-01T00:00:00',
    CreatedByUserId: 0,
    Modified: '0001-01-01T00:00:00',
    ModifiedByUserId: 0,
    Timestamp: null,
    AllowAudit: false,
    AllowCreate: false,
    AllowDelete: false,
    AllowEdit: true,
    AllowRegiezentrum: true,
    GroupId: groupId,
    ViewId: viewId,
  };
}

/** Rumpf des PUT ohne die Navigationsobjekte, die `GET groups/{id}/views` mitliefert. */
function groupViewBody(row: ChinaAppSysGroupView): ChinaAppSysGroupViewBody {
  return {
    Id: row.Id,
    Created: row.Created,
    CreatedByUserId: row.CreatedByUserId,
    Modified: row.Modified,
    ModifiedByUserId: row.ModifiedByUserId,
    Timestamp: row.Timestamp,
    AllowAudit: row.AllowAudit,
    AllowCreate: row.AllowCreate,
    AllowDelete: row.AllowDelete,
    AllowEdit: row.AllowEdit,
    AllowRegiezentrum: row.AllowRegiezentrum,
    GroupId: row.GroupId,
    ViewId: row.ViewId,
  };
}

/** Ein Recht umschalten, ohne den Rest des Satzes anzufassen. */
function withRight(row: ChinaAppSysGroupView, key: RightKey, value: boolean) {
  const next = { ...row };
  next[key] = value;
  return next;
}

function viewName(row: ChinaAppSysGroupView): string {
  return row.ViewBezeichnung ?? row.View?.ViewName ?? `View ${String(row.ViewId)}`;
}

export function AdminGroupViewsList(props: AdminGroupViewsListProps) {
  const { parentId } = props;
  const changes = useAdminChanges();
  const load = useCallback(() => groupApi.getViews(parentId), [parentId]);
  const { rows, setRows, loading, error, reload } = useAdminList(load);
  const [options, setOptions] = useState<PickerOption[] | null>(null);
  const [busy, setBusy] = useState(false);

  const taken = useMemo(() => rows.map((row) => row.ViewId), [rows]);

  const openPicker = async () => {
    setBusy(true);
    const all = await viewApi.get();
    setOptions(
      all
        .filter((view) => !taken.includes(view.Id))
        .map((view) => ({
          id: view.Id,
          label: view.ViewName ?? '',
          description: entityTypeDisplayNames[view.Entity],
        })),
    );
    setBusy(false);
  };

  const change = async (run: () => Promise<unknown>) => {
    setBusy(true);
    await run();
    await reload();
    setBusy(false);
    changes.notifyNavigationChanged();
  };

  const replace = (row: ChinaAppSysGroupView) => {
    setRows((current) => current.map((entry) => (entry.Id === row.Id ? row : entry)));
  };

  /** Ein Kästchen speichert sofort — optimistisch, mit Rückrollen bei Misserfolg. */
  const toggle = async (row: ChinaAppSysGroupView, key: RightKey, value: boolean) => {
    const next = withRight(row, key, value);
    replace(next);
    if (!(await groupApi.putView(parentId, row.Id, groupViewBody(next)))) {
      replace(row);
      return;
    }
    changes.notifyNavigationChanged();
  };

  return (
    <>
      <AdminSubList<ChinaAppSysGroupView>
        title="Views"
        columns={COLUMNS}
        rows={rows}
        rowKey={(row) => row.Id}
        renderCells={(row) => (
          <>
            <td>{viewName(row)}</td>
            {RIGHTS.map((right) => (
              <td key={right.key} className="iwm-admin-sublist-center">
                <CheckBox
                  className="iwm-admin-sublist-check"
                  label={`${right.label} für ${viewName(row)}`}
                  checked={row[right.key]}
                  onChange={(checked) => {
                    void toggle(row, right.key, checked);
                  }}
                />
              </td>
            ))}
          </>
        )}
        loading={loading}
        error={error}
        onRetry={() => {
          void reload();
        }}
        busy={busy}
        onAdd={() => {
          void openPicker();
        }}
        onRemove={(row) => {
          void change(() => groupApi.removeView(parentId, row.ViewId));
        }}
        emptyText="Keine Views zugeordnet"
        errorMessage="Die Views konnten nicht geladen werden."
      />
      <PickerDialog
        open={options !== null}
        title="Views auswählen"
        options={options ?? []}
        emptyText="Der Gruppe sind bereits alle Views zugeordnet."
        onClose={() => {
          setOptions(null);
        }}
        onSubmit={async (ids) => {
          await change(async () => {
            for (const id of ids) await groupApi.addView(parentId, createGroupView(parentId, id));
          });
        }}
      />
    </>
  );
}

export default AdminGroupViewsList;
